import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { logActivity } from '../lib/activityLogger';
import { useSystemStatus } from '../contexts/SystemStatusContext';
import MaintenancePage from './Maintenance';
import { publicApiClient } from '../apiClient';
import SEO from '../components/common/SEO';
import Breadcrumbs from '../components/common/Breadcrumbs';

const Login = () => {
  const { t } = useTranslation('translation');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const { isMaintenanceActive } = useSystemStatus();

  // State for Password Reset Modal
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoggingIn(true);

    try {
      try {
        const preCheck = await publicApiClient.post('/v1/auth/pre-login-check', { email });
        const checkResult = await preCheck.json();
        if (!preCheck.ok) {
          setError(checkResult.message || t('login.genericError'));
          setIsLoggingIn(false);
          return;
        }
      } catch (checkErr) {
        console.error("Security check failed:", checkErr);
      }

      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const db = getFirestore();
      const userDocRef = doc(db, 'users', user.uid);

      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      if (isMaintenanceActive) {
          const idTokenResult = await user.getIdTokenResult();
          if (!idTokenResult.claims.systemadmin) {
              await auth.signOut();
              setError(t('maintenance_mode', '現在システムメンテナンス中です。'));
              setIsLoggingIn(false);
              return;
          }
      }

      try {
        await publicApiClient.post('/v1/auth/login-success', { email });
      } catch (err) {
        console.error("Error resetting login failures:", err);
      }

      try {
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp()
        });
      } catch (updateError) {
        console.error("Error updating lastLogin:", updateError);
      }

      try {
        if (userDoc.exists()) {
          const billwaveCustomerId = userData.billwaveCustomerId;
          const actorName = userData.representativeName;

          if (billwaveCustomerId) {
            await logActivity({
              billwaveCustomerId: billwaveCustomerId,
              action: 'member_login_success',
              result: 'success',
              actor: {
                uid: user.uid,
                email: user.email,
                name: actorName || null,
              },
              target: {
                type: 'user',
                id: user.uid,
                name: actorName || null,
              },
            });
          }
        }
      } catch (logError) {
        console.error("An error occurred while trying to log the login event:", logError);
      }

      sessionStorage.setItem('loginTimestamp', Date.now().toString());
      navigate('/dashboard');

    } catch (err) {
      console.error("Login error:", err.code);

      try {
        await publicApiClient.post('/v1/auth/login-failure', { email });
      } catch (trackErr) {
        console.error("Error tracking login failure:", trackErr);
      }

      logActivity({
        billwaveCustomerId: 'unknown',
        action: 'member_login_failed',
        result: 'failed',
        actor: { uid: 'unknown', email: email },
        details: { error_code: err.code },
      });

      switch (err.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError(t('login.invalidCredentialError'));
          break;
        default:
          setError(t('login.genericError'));
          break;
      }
      setIsLoggingIn(false);
    }
  };

  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    setResetError(null);
    setIsSendingReset(true);

    try {
      const res = await publicApiClient.post('/v1/auth/password-reset-request', { email: resetEmail });
      const data = await res.json();
      if (!res.ok) {
        setResetError(data.message || t('login.genericError'));
      } else {
        setResetSuccess(true);
      }
    } catch (err) {
      console.error('Password reset request error:', err);
      setResetError(t('login.genericError'));
    } finally {
      setIsSendingReset(false);
    }
  };

  const openResetModal = () => {
    setResetEmail(email);
    setResetError(null);
    setResetSuccess(false);
    setIsResetModalOpen(true);
  };

  if (isMaintenanceActive) {
      return <MaintenancePage />;
  }

  return (
    <>
      <SEO 
        title="ログイン｜Stripe請求書CSV一括発行サービス Billwave"
        description="Stripe請求書一括発行クラウド「Billwave」のログインページです。登録済みのメールアドレスとパスワードを入力して、請求書の一括アップロードや予約発行管理画面へアクセスしてください。"
        path="/login"
      />
      <div className="w-full max-w-md mx-auto py-4 px-2">
        <Breadcrumbs items={[{ label: 'ログイン' }]} />

        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md border border-gray-100">
          <h1 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-6 pb-2 border-b-2 border-gray-200">
            {t('login.loginTitle')}
          </h1>
          <form onSubmit={handleLogin} className="flex flex-col gap-5">
            <div>
              <label htmlFor="email" className="block mb-1 font-bold text-gray-700">
                {t('login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                className="w-full p-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoggingIn}
              />
            </div>
            <div>
              <label htmlFor="password" className="block mb-1 font-bold text-gray-700">
                {t('login.passwordLabel')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                className="w-full p-3 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoggingIn}
              />
            </div>
            <button
              type="submit"
              className={`w-full p-3 text-lg font-semibold text-white rounded-md shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-2 ${isLoggingIn ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? t('login.loggingIn', 'ログイン中...') : t('login.loginButton')}
            </button>
            <div className="text-center mt-1">
              <button
                type="button"
                onClick={openResetModal}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
              >
                {t('login.forgotPassword', 'パスワードをお忘れですか？')}
              </button>
            </div>
            {error && <p className="text-red-600 text-center mt-2 p-2 bg-red-100 rounded-md text-sm">{error}</p>}
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center space-y-3">
            <p className="text-sm text-gray-600">
              まだアカウントをお持ちでない方はこちら
            </p>
            <Link to="/signup" className="inline-block w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-md text-sm transition-colors">
              無料で新規登録（毎月5件まで無料）
            </Link>
            <div className="pt-2">
              <Link to="/support/how-to-use" className="text-xs text-blue-600 hover:underline">
                使い方・利用ガイドを見る
              </Link>
            </div>
          </div>
        </div>

        {/* Password Reset Modal */}
        {isResetModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <h2 className="text-xl font-bold text-gray-800 mb-3">
                {t('login.resetPasswordTitle', 'パスワードの再設定')}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('login.resetPasswordDescription', 'ご登録のメールアドレスを入力してください。パスワード再設定用のリンクをお送りします。')}
              </p>

              {resetSuccess ? (
                <div>
                  <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm text-center">
                    {t('login.resetSuccessMessage', 'パスワード再設定用のメールを送信しました。メールをご確認ください。')}
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={() => setIsResetModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none"
                    >
                      {t('login.close', '閉じる')}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePasswordResetRequest} className="space-y-4">
                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('login.emailLabel', 'メールアドレス')}
                    </label>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder={t('login.emailPlaceholder', 'メールアドレスを入力')}
                      required
                      className="w-full p-2.5 text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSendingReset}
                    />
                  </div>

                  {resetError && (
                    <p className="text-red-600 text-sm p-2 bg-red-100 rounded-md text-center">
                      {resetError}
                    </p>
                  )}

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setIsResetModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none"
                      disabled={isSendingReset}
                    >
                      {t('login.cancel', 'キャンセル')}
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isSendingReset ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                      disabled={isSendingReset}
                    >
                      {isSendingReset ? t('submitButtonLoading', '送信中...') : t('login.sendResetLink', '送信する')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Login;
